'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { authClient } from '@/auth/client';
import { EkutuQrLogo } from '@/components/EkutuQrLogo';
import {
	Mail,
	Lock,
	User,
	Eye,
	EyeOff,
	Sparkles,
	ShieldCheck,
	ArrowRight,
	Loader2,
	AlertCircle,
	CheckCircle2,
} from 'lucide-react';

export function EmailPasswordForm({
	mode,
	callbackUrl,
	verified = false,
}: {
	mode: 'sign-in' | 'sign-up';
	callbackUrl?: string;
	verified?: boolean;
}) {
	const isSignUp = mode === 'sign-up';
	const router = useRouter();
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [canResendVerification, setCanResendVerification] = useState(false);
	const [resending, setResending] = useState(false);
	const callbackURL = callbackUrl ?? '/dashboard/qr-codes';

	async function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setBusy(true);
		setError('');
		setMessage('');
		setCanResendVerification(false);
		try {
			if (isSignUp) {
				const result = await authClient.signUp.email({
					name: name.trim(),
					email: email.trim(),
					password,
					callbackURL: `/sign-in?verified=1&callbackURL=${encodeURIComponent(callbackURL)}`,
				});
				if (result.error) throw new Error(result.error.message || 'Could not create your account.');
				setMessage(
					'Check your email for a verification link. After verifying, sign in to continue.',
				);
			} else {
				const result = await authClient.signIn.email({ email: email.trim(), password });
				if (result.error) {
					setCanResendVerification(result.error.code === 'EMAIL_NOT_VERIFIED');
					throw new Error(result.error.message || 'Email or password is incorrect.');
				}
				router.replace(
					callbackURL.startsWith('/') && !callbackURL.startsWith('//')
						? callbackURL
						: '/dashboard/qr-codes',
				);
				router.refresh();
			}
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Something went wrong. Please try again.');
		} finally {
			setBusy(false);
		}
	}

	async function resendVerification() {
		setResending(true);
		setError('');
		setMessage('');
		try {
			const result = await authClient.sendVerificationEmail({
				email: email.trim(),
				callbackURL: `/sign-in?verified=1&callbackURL=${encodeURIComponent(callbackURL)}`,
			});
			if (result.error)
				throw new Error(result.error.message || 'Unable to resend the verification email.');
			setMessage('If this account is eligible, a new verification email has been sent.');
			setCanResendVerification(false);
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Unable to resend the verification email.');
		} finally {
			setResending(false);
		}
	}

	return (
		<main className="relative min-h-[calc(100vh-140px)] flex items-center justify-center px-4 py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-slate-50 via-emerald-50/20 to-slate-100/60">
			{/* Ambient aurora glow elements */}
			<div className="pointer-events-none absolute -top-40 -left-40 size-[450px] rounded-full bg-emerald-400/15 blur-3xl" />
			<div className="pointer-events-none absolute -bottom-40 -right-40 size-[450px] rounded-full bg-teal-500/10 blur-3xl" />
			<div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] rounded-full bg-purple-500/5 blur-3xl" />

			<div className="relative z-10 w-full max-w-md">
				{/* Frosted Aurora Card */}
				<div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/80 shadow-xl shadow-slate-200/50 p-6 sm:p-9 transition-all">
					{/* Logo & Value Badge */}
					<div className="text-center mb-6">
						<Link
							href="/"
							className="inline-block transition-transform hover:scale-105 active:scale-95 mb-3"
							title="Ekutu QR"
						>
							<EkutuQrLogo size="default" />
						</Link>

						<div className="flex justify-center mb-3">
							<span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-[11px] font-semibold shadow-2xs">
								<span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
								100% Free • Unlimited Scans
							</span>
						</div>

						<h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
							{isSignUp ? 'Create your free account' : 'Welcome back'}
						</h1>
						<p className="mt-1.5 text-xs sm:text-sm text-slate-500">
							{isSignUp
								? 'Create, manage, and track unlimited dynamic QR codes with zero paywalls.'
								: 'Sign in to access your QR codes, analytics, and leads.'}
						</p>
					</div>

					{/* Fast Mode Switcher Pill */}
					<div className="grid grid-cols-2 p-1 bg-slate-100/80 rounded-xl mb-6 text-xs font-semibold text-slate-600">
						<Link
							href={`/sign-in${callbackUrl ? `?callbackURL=${encodeURIComponent(callbackUrl)}` : ''}`}
							className={`py-2 text-center rounded-lg transition-all ${
								!isSignUp ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'hover:text-slate-900'
							}`}
						>
							Sign In
						</Link>
						<Link
							href={`/sign-up${callbackUrl ? `?callbackURL=${encodeURIComponent(callbackUrl)}` : ''}`}
							className={`py-2 text-center rounded-lg transition-all ${
								isSignUp ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'hover:text-slate-900'
							}`}
						>
							Create Account
						</Link>
					</div>

					{/* Email Verified Banner */}
					{verified && (
						<div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200/80 p-3 text-xs font-medium text-emerald-800">
							<CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
							<span>Email verified successfully. You can sign in now.</span>
						</div>
					)}

					{/* Form */}
					<form className="space-y-4" onSubmit={submit}>
						{isSignUp && (
							<div>
								<label className="block text-xs font-semibold text-slate-700 mb-1.5">
									Full name
								</label>
								<div className="relative">
									<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
										<User className="size-4" />
									</div>
									<input
										className="w-full rounded-xl border border-slate-200 bg-white/70 hover:bg-white pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all"
										placeholder="e.g. Alex Morgan"
										autoComplete="name"
										required
										maxLength={255}
										value={name}
										onChange={(event) => setName(event.target.value)}
									/>
								</div>
							</div>
						)}

						<div>
							<label className="block text-xs font-semibold text-slate-700 mb-1.5">
								Email address
							</label>
							<div className="relative">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
									<Mail className="size-4" />
								</div>
								<input
									className="w-full rounded-xl border border-slate-200 bg-white/70 hover:bg-white pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all"
									placeholder="you@example.com"
									type="email"
									autoComplete="email"
									required
									maxLength={255}
									value={email}
									onChange={(event) => setEmail(event.target.value)}
								/>
							</div>
						</div>

						<div>
							<div className="flex items-center justify-between mb-1.5">
								<label className="block text-xs font-semibold text-slate-700">Password</label>
								{!isSignUp && (
									<Link
										href="/forgot-password"
										className="text-xs font-medium text-[#0f766e] hover:text-[#115e59] hover:underline"
									>
										Forgot password?
									</Link>
								)}
							</div>
							<div className="relative">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
									<Lock className="size-4" />
								</div>
								<input
									className="w-full rounded-xl border border-slate-200 bg-white/70 hover:bg-white pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0f766e] focus:ring-2 focus:ring-[#0f766e]/20 transition-all"
									placeholder={isSignUp ? 'Minimum 8 characters' : '••••••••'}
									type={showPassword ? 'text' : 'password'}
									autoComplete={isSignUp ? 'new-password' : 'current-password'}
									required
									minLength={8}
									value={password}
									onChange={(event) => setPassword(event.target.value)}
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
									title={showPassword ? 'Hide password' : 'Show password'}
								>
									{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
								</button>
							</div>
						</div>

						{/* Error Banner */}
						{error && (
							<div
								role="alert"
								className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200/80 p-3 text-xs text-red-700"
							>
								<AlertCircle className="size-4 text-red-500 shrink-0 mt-0.5" />
								<span>{error}</span>
							</div>
						)}

						{/* Resend Verification Action */}
						{canResendVerification && (
							<div className="text-center">
								<button
									className="text-xs font-semibold text-[#0f766e] hover:underline disabled:opacity-60 cursor-pointer"
									type="button"
									onClick={() => void resendVerification()}
									disabled={resending}
								>
									{resending ? 'Sending verification email…' : 'Resend verification email'}
								</button>
							</div>
						)}

						{/* Status/Success Message */}
						{message && (
							<div
								role="status"
								className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200/80 p-3 text-xs text-emerald-800"
							>
								<CheckCircle2 className="size-4 text-emerald-600 shrink-0 mt-0.5" />
								<span>{message}</span>
							</div>
						)}

						{/* Primary Action Button */}
						<button
							className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#0f766e] to-[#047857] hover:from-[#115e59] hover:to-[#065f46] shadow-sm hover:shadow-md transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer mt-2"
							type="submit"
							disabled={busy}
						>
							{busy ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									<span>Please wait…</span>
								</>
							) : (
								<>
									<span>{isSignUp ? 'Create Free Account' : 'Sign In'}</span>
									<ArrowRight className="size-4" />
								</>
							)}
						</button>
					</form>

					{/* Bottom Alternative Link */}
					<div className="mt-6 text-center text-xs text-slate-500">
						{isSignUp ? (
							<p>
								Already have an account?{' '}
								<Link
									href={`/sign-in${callbackUrl ? `?callbackURL=${encodeURIComponent(callbackUrl)}` : ''}`}
									className="font-semibold text-[#0f766e] hover:underline"
								>
									Sign in
								</Link>
							</p>
						) : (
							<p>
								Don't have an account yet?{' '}
								<Link
									href={`/sign-up${callbackUrl ? `?callbackURL=${encodeURIComponent(callbackUrl)}` : ''}`}
									className="font-semibold text-[#0f766e] hover:underline"
								>
									Create free account
								</Link>
							</p>
						)}
					</div>

					{/* Trust Badges */}
					<div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-center gap-3 text-[11px] text-slate-600 font-medium">
						<span className="flex items-center gap-1">
							<ShieldCheck className="size-3.5 text-emerald-600" />
							<span>256-bit SSL</span>
						</span>
						<span className="text-slate-300">•</span>
						<span className="flex items-center gap-1">
							<Sparkles className="size-3.5 text-emerald-600" />
							<span>100% Free</span>
						</span>
						<span className="text-slate-300">•</span>
						<span>No Credit Card</span>
					</div>
				</div>
			</div>
		</main>
	);
}
